/**
 * OpenAI Ads Measurement Pixel helper.
 *
 * The pixel is intentionally restricted to production hostnames so preview /
 * sandbox traffic does not pollute OpenAI Ads campaign reporting.
 */

const OPENAI_PIXEL_ID = "PQq4ZSkkBkHkFmauYHH3WM";

const PROD_HOSTS = new Set([
  "coloradowebimpressions.com",
  "www.coloradowebimpressions.com",
]);

interface OaiqFn {
  (...args: unknown[]): void;
  q?: unknown[][];
}

declare global {
  interface Window {
    oaiq?: OaiqFn;
  }
}

/**
 * Initialize the OpenAI pixel stub. Safe to call repeatedly; only runs in
 * the browser and only on production hostnames.
 */
export function initOpenAIPixel() {
  if (typeof window === "undefined") return;
  if (!PROD_HOSTS.has(window.location.hostname)) return;

  window.oaiq =
    window.oaiq ||
    ((...args: unknown[]) => {
      (window.oaiq!.q = window.oaiq!.q || []).push(args);
    });

  window.oaiq("init", { pixelId: OPENAI_PIXEL_ID });
}

/**
 * Fire a browser conversion event to OpenAI Ads.
 *
 * @param eventType   Event name, e.g. "lead_created".
 * @param data        OpenAI event data payload.
 * @param opts        value/currency/event_id for deduplication and reporting.
 */
export function measureOpenAI(
  eventType: string,
  data: Record<string, unknown> = {},
  opts: { event_id?: string; value?: number; currency?: string } = {},
) {
  if (typeof window === "undefined") return;
  if (!PROD_HOSTS.has(window.location.hostname)) return;
  if (typeof window.oaiq !== "function") return;

  const payload: Record<string, unknown> = { ...data };
  if (opts.value !== undefined) payload.value = opts.value;
  if (opts.currency) payload.currency = opts.currency;

  const measureOpts: Record<string, unknown> = {};
  if (opts.event_id) measureOpts.event_id = opts.event_id;

  window.oaiq("measure", eventType, payload, measureOpts);
}

/**
 * Fire an OpenAI `page_view` event. Called on initial load and on every
 * client-side route change so the data source has a steady event stream.
 */
export function measureOpenAIPageView(path?: string) {
  if (typeof window === "undefined") return;
  measureOpenAI("page_view", {
    source_url: path
      ? `${window.location.origin}${path}`
      : window.location.href,
  });
}
